// React hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Helpers i API servisi
import { transformRoute, useTranslate } from "helpers";

import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Tipovi i konstante
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";

import { VehicleTireSetSearchPageCmp, VehicleTireSetSearchPageProps } from "./types";
import {
  FLEET_VEHICLE_TIRE_SET_CREATE_PAGE_TITLE,
  FLEET_VEHICLE_TIRE_SET_EDIT_PAGE_TITLE,
  FLEET_VEHICLE_TIRE_SET_SEARCH_PAGE_TITLE,
} from "ui/components/PageTitle/consts";

// UI komponente
import { Button } from "ui/components/Button/Button";
import { NoResults } from "ui/components/NoResults";

// Moduli specifični za projekt
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";
import TabMenu from "ui/modules/fleet/vehicles/pages/components/TabMenu/TabMenu";
import { VehicleTabHeader } from "ui/modules/fleet/vehicles/pages/components/VehicleTabHeader";

// Lokalne komponente
import VehicleTireSetSearchFilters from "./components/VehicleTireSetSearchFilters/VehicleTireSetSearchFilters";
import VehicleTireSetSearchResults from "../VehicleTireSetCard/components/VehicleTireSearchResults/VehicleTireSetSearchResults";
import { deleteFleetTireSetAPI, getFleetTiresSetAPI } from "services/api/fleet/tires/api";
import { Dialog } from "ui/components/Dialog";
import { FleetTireSetLocationEntityType, FleetTireSetRow } from "services/api/fleet/tires/types";
import TireSetEditCard from "ui/modules/fleet/tires/pages/TireSetEditCard/TireSetEditCard";
import TireSetDemountEditCard from "ui/modules/fleet/tires/pages/TireSetDemountEditCard/TireSetDemountEditCard";
import { FleetVehicleDetailFormValues, FleetVehicleStatusEnum } from "services/api/fleet/vehicles/types";
import { initialFleetVehicleDetailForm } from "services/api/fleet/vehicles/consts";
import { Errors } from "services/forms/validation/types";
import { getFleetVehicleAPI } from "services/api/fleet/vehicles/api";

const VehicleTireSetSearchPage: VehicleTireSetSearchPageCmp = (
  props: VehicleTireSetSearchPageProps
) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<FleetVehicleDetailFormValues>(
    initialFleetVehicleDetailForm
  );
  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetTireSetRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [tireSetId, setTireSetId] = useState<string>("");
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [showDemount, setShowDemount] = useState(false);
  const [transferTireSetId, setTransferTireSetId] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  useEffect(() => {
    if (id) {
      fetchVehicle(id);
      fetchSearchResult(id, searchResults.pagination);
    }
  }, [id]);



  const fetchVehicle = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialFleetVehicleDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getFleetVehicleAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const fetchSearchResult = async (id: string, pagination: Pagination) => {
    setIsLoading(true);
    try {
      const results = await getFleetTiresSetAPI(pagination, null, id);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    setTireSetId(id);
    setShowAddNewModal(true);
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setTireSetId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleEditSuccess = useCallback(() => {
    if (id) fetchSearchResult(id, searchResults.pagination);

    setTireSetId("");
    setShowAddNewModal(false);
  }, []);

  const handleSearchChange = useCallback(() => {
    if (id) fetchSearchResult(id, searchResults.pagination);
  }, []);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    if (id) fetchSearchResult(id, newPagination);
  };

  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setTireSetId(id);
    setOpenDialog(true);
  }

  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!tireSetId) return;

    try {
      const deleted = await deleteFleetTireSetAPI(tireSetId, t);

      if (deleted && id) {
        await fetchSearchResult(id, searchResults.pagination);
      }

      // zatvori dijalog nakon brisanja
      setOpenDialog(false);
      setTireSetId("");
    } catch (err) {
      console.error("Greška prilikom brisanja seta guma:", err);
    }
  }, [tireSetId, t]);

  const handleDeleteDialogClose = useCallback(() => {
    setTireSetId("");
    setOpenDialog(false);
  }, []);

  const handleLocationClick = useCallback((event: React.MouseEvent<HTMLSpanElement, MouseEvent>, entityId: string, entityName: string) => {
    event.stopPropagation();
    event.preventDefault();

    let route: string;

    switch (entityName) {
      case FleetTireSetLocationEntityType.Vehicle:
        route = transformRoute(
          FleetVehicleRoutes.FleetVehicleDetailPageRouter,
          { id: entityId }
        );
        break;
      case FleetTireSetLocationEntityType.Warehouse:
        route = transformRoute(
          "/fleet/warehouse/:id/detail/",
          { id: entityId }
        );
        break;
      default:
        return;
    }

    navigate(route);
  }, [navigate]);

  const handleEditClick = useCallback((event: React.MouseEvent<HTMLSpanElement, MouseEvent>, tireSetId: string) => {
    event.stopPropagation();
    event.preventDefault();
    setTireSetId(tireSetId);
    setShowAddNewModal(true);
  }, []);

  const handleTransferClick = useCallback((event: React.MouseEvent<HTMLSpanElement, MouseEvent>, tireSetId: string) => {
    event.stopPropagation();
    event.preventDefault();
    setTransferTireSetId(tireSetId);
    setShowDemount(true);
  }, []);

  const handleTransferSuccess = useCallback(() => {
    if (id) fetchSearchResult(id, searchResults.pagination);
    setTransferTireSetId("");
    setShowDemount(false);
  }, [id, searchResults.pagination]);

  // Promjena statusa preko značke — vozilo se dohvaća ponovno da se značka osvježi.
  const handleStatusChange = useCallback(() => {
    if (id) fetchVehicle(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!id) return <></>;

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <VehicleTabHeader
            vehicleId={id}
            vehicle={formValues}
            title={t(FLEET_VEHICLE_TIRE_SET_SEARCH_PAGE_TITLE.text)}
            icon={FLEET_VEHICLE_TIRE_SET_SEARCH_PAGE_TITLE.icon}
            iconColor={FLEET_VEHICLE_TIRE_SET_SEARCH_PAGE_TITLE.iconColor}
            onStatusChange={handleStatusChange}
          >
            {/* Prodano vozilo je arhivirano — nove garniture se ne dodaju. */}
            {formValues.status !== FleetVehicleStatusEnum.SOLD && (
              <Button
                variant="primary"
                children={t("admin.fleet.tire.set.search.add")}
                startIcon="/media/icons/plus.svg"
                onClick={handleAddNew}
              />
            )}
          </VehicleTabHeader>
        </div>
        <TabMenu vehicleId={id} />
        <div className="px-0 py-6 mx-[-1px] xl:px-6">
          <VehicleTireSetSearchFilters onSearchChange={handleSearchChange} />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="Unesite vozilo klikom na 'Dodaj gume'" />
          ) : (
            <VehicleTireSetSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
              onDeleteClick={handleDeleteClick}
              onLocationClick={handleLocationClick}
              onEditClick={handleEditClick}
              onDemountClick={handleTransferClick}
            />
          )}
        </div>
      </div>
      <Dialog
        open={openDialog}
        title={t(
          "admin.fleet.vehicle.tire.set.dialog.delete.title"
        )}
        message={t(
          "admin.fleet.vehicle.tire.set.dialog.delete.message"
        )}
        confirmText={t(
          "admin.fleet.vehicle.tire.set.dialog.delete.confirmtext"
        )}
        cancelText={t(
          "admin.fleet.vehicle.tire.set.dialog.delete.canceltext"
        )}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
      <TireSetEditCard
        show={showAddNewModal}
        vehicleId={id}
        tireSetId={tireSetId}
        modalIcon="/media/images/add-icon.svg"
        modalTitle={
          tireSetId
            ? FLEET_VEHICLE_TIRE_SET_EDIT_PAGE_TITLE.text
            : FLEET_VEHICLE_TIRE_SET_CREATE_PAGE_TITLE.text
        }
        modalPosition="side"
        onSuccess={handleEditSuccess}
        onClose={() => setShowAddNewModal(false)}
      />
      <TireSetDemountEditCard
        show={showDemount}
        tireSetId={transferTireSetId}
        onSuccess={handleTransferSuccess}
        onClose={() => { setShowDemount(false); setTransferTireSetId(""); }}
      />
    </>
  );
};

VehicleTireSetSearchPage.route =
  FleetVehicleRoutes.FleetVehicleTireSetSearchPageRouter;

export default VehicleTireSetSearchPage;
